import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useConnection } from 'wagmi';

import { DASH, TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { formatApy, formatDateTime, formatTokenAmount, formatUnlockDuration, formatUsd } from '@/app/shared/lib/formatters';
import { resolveTrancheDisplayLabel } from '@/app/shared/lib/tranche';
import { getUserPositions } from '@/app/shared/services/api';
import { UserPosition } from '@/app/shared/services/api/types';

export interface MarketPositionRow {
  id: string;
  name: string;
  tranche?: string;
  tokenBalance: string;
  deposited: string;
  currentValue: string;
  apy: string;
  yieldEarned: string;
  terms: string;
}

const formatTerms = (value?: string | number): string => {
  if (value == null || value === '') return DASH;
  const seconds = Number(value);
  if (Number.isNaN(seconds)) return DASH;
  return formatUnlockDuration(seconds);
};

const formatTrancheSecondary = (trancheLabel: string, symbol?: string): string => {
  const trimmed = symbol?.trim();
  if (!trimmed) return trancheLabel;
  return `${trancheLabel} · ${trimmed}`;
};

const formatUpdatedAt = (value?: string): string => {
  if (!value) return DASH;
  const formatted = formatDateTime(value);
  return formatted === 'N/A' ? DASH : formatted;
};

const mapPositionToRow = (position: UserPosition, index: number): MarketPositionRow => {
  const trancheLabel = resolveTrancheDisplayLabel(position.trancheName);

  return {
    id: `${position.vaultAddress}-${position.trancheName}-${index}`,
    name: position.vaultName || DASH,
    tranche: formatTrancheSecondary(trancheLabel, position.trancheSymbol),
    tokenBalance: formatTokenAmount(position.tokenBalance, TRANCHE_SHARE_DECIMALS),
    deposited: formatUsd(position.depositedUsd),
    currentValue: formatUsd(position.currentValueUsd),
    apy: formatApy(position.currentApy),
    yieldEarned: formatUsd(position.yieldEarnedUsd),
    terms: formatTerms(position.withdrawalTerms),
  };
};

export const useMarketPositions = (vaultId?: string) => {
  const { address } = useConnection();

  const { data, isLoading, error } = useQuery({
    queryKey: [QueryKeys.USER_POSITIONS, address, vaultId ?? 'all'],
    queryFn: () => getUserPositions({ address, vault: vaultId }),
    enabled: !!address,
    staleTime: Interval.ONE_MIN,
    refetchInterval: Interval.ONE_MIN,
  });

  const positions = useMemo(() => {
    const list = data?.positions ?? [];
    const filtered = vaultId
      ? list.filter((position) => position.vaultAddress?.toLowerCase() === vaultId.toLowerCase())
      : list;

    return filtered.map((position, index) => {
      const row = mapPositionToRow(position, index);
      if (vaultId) {
        return {
          ...row,
          name: resolveTrancheDisplayLabel(position.trancheName),
          tranche: undefined,
        };
      }
      return row;
    });
  }, [data?.positions, vaultId]);

  return {
    positions,
    updatedAt: formatUpdatedAt(data?.updated),
    isLoading,
    error,
    isConnected: !!address,
  };
};
